import { useState } from 'react';
import { Sparkles, Star, Home, User, TrendingUp, Settings, BarChart3 } from 'lucide-react';
import { AppProvider, useApp } from './lib/AppContext';
import { ProfileSetup } from './components/ui/ProfileSetup';
import { LevelSelect } from './components/ui/LevelSelect';
import { Celebration } from './components/ui/Celebration';
import { SettingsDrawer } from './components/ui/SettingsDrawer';
import { AdminDashboard } from './components/ui/AdminDashboard';
import { WordMatchGame } from './components/game/WordMatchGame';
import { SpellItGame } from './components/game/SpellItGame';
import { MissingLetterGame } from './components/game/MissingLetterGame';
import { GameType, DifficultyLevel } from './types';

type View = 'home' | 'level-select' | 'game' | 'admin';

function AppContent() {
  const { userProfile, userProgress, gameState, startGame } = useApp();
  const [currentView, setCurrentView] = useState<View>('home');
  const [selectedGameType, setSelectedGameType] = useState<GameType | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Show profile setup if no profile exists
  if (!userProfile) {
    return <ProfileSetup />;
  }

  const handleGameSelect = (gameType: GameType) => {
    setSelectedGameType(gameType);
    setCurrentView('level-select');
  };

  const handleLevelSelect = (level: DifficultyLevel) => {
    if (!selectedGameType || !userProfile) return;

    // Start the game via context
    startGame(selectedGameType, level, userProfile.preferredLanguage);
    setCurrentView('game');
  };

  const handleBackToHome = () => {
    setCurrentView('home');
    setSelectedGameType(null);
  };

  const renderGame = () => {
    if (!gameState.currentSession) return null;

    switch (gameState.currentSession.gameType) {
      case 'word-match':
        return <WordMatchGame />;
      case 'spell-it':
        return <SpellItGame />;
      case 'missing-letter':
        return <MissingLetterGame />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-400 via-pink-400 to-blue-400 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <header className="bg-white rounded-b-3xl shadow-2xl p-6 mb-8 animate-slideUp">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className="text-4xl">{userProfile.avatar}</div>
              <div>
                <h1 className="text-3xl font-bold text-primary flex items-center gap-2">
                  Word Adventure
                  <Sparkles className="text-yellow-500" size={24} />
                </h1>
                <p className="text-gray-600">Welcome back, {userProfile.name}! 👋</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {userProgress && userProgress.currentStreak > 0 && (
                <div className="text-right">
                  <div className="flex items-center gap-2 justify-end">
                    <span className="text-3xl">🔥</span>
                    <span className="text-3xl font-bold text-orange-600">
                      {userProgress.currentStreak}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">Day Streak</p>
                </div>
              )}
              <div className="text-right">
                <div className="flex items-center gap-2 justify-end">
                  <Star className="text-yellow-500 fill-yellow-500" />
                  <span className="text-3xl font-bold text-yellow-600">
                    {userProgress?.totalScore || 0}
                  </span>
                </div>
                <p className="text-sm text-gray-500">Total Score</p>
              </div>
              <button
                onClick={() => setSettingsOpen(true)}
                className="p-3 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 text-white hover:shadow-lg transition-all duration-200 hover:scale-105"
                aria-label="Settings"
              >
                <Settings size={24} />
              </button>
            </div>
          </div>

          {/* Navigation */}
          {currentView !== 'home' && (
            <div className="mt-4">
              <button
                onClick={handleBackToHome}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
              >
                <Home size={20} />
                Back to Home
              </button>
            </div>
          )}
        </header>

        {/* Main Content */}
        <main className="animate-fadeIn">
          {currentView === 'home' && (
            <div>
              {/* Game Selection */}
              <div className="bg-white rounded-3xl shadow-2xl p-8 mb-6">
                <h2 className="text-4xl font-bold text-primary text-center mb-2">
                  Choose Your Game
                </h2>
                <p className="text-xl text-gray-600 text-center mb-8">
                  Pick a game mode to start learning!
                </p>

                <div className="grid md:grid-cols-3 gap-6">
                  <button
                    onClick={() => handleGameSelect('word-match')}
                    className="game-card group"
                  >
                    <div className="text-6xl mb-4 group-hover:scale-110 transition-transform">
                      ✨
                    </div>
                    <h3 className="text-2xl font-bold text-blue-600 mb-2">Word Match</h3>
                    <p className="text-gray-600 mb-4">
                      Listen to the word and select the correct match from the options!
                    </p>
                    <div className="text-sm text-gray-500">
                      🎯 Accuracy • 🔊 Listening • ⚡ Speed
                    </div>
                  </button>

                  <button
                    onClick={() => handleGameSelect('spell-it')}
                    className="game-card group"
                  >
                    <div className="text-6xl mb-4 group-hover:scale-110 transition-transform">
                      🏆
                    </div>
                    <h3 className="text-2xl font-bold text-green-600 mb-2">Spell It!</h3>
                    <p className="text-gray-600 mb-4">
                      Listen carefully and type the word you hear correctly!
                    </p>
                    <div className="text-sm text-gray-500">
                      ⌨️ Typing • 🎧 Listening • 🧠 Memory
                    </div>
                  </button>

                  <button
                    onClick={() => handleGameSelect('missing-letter')}
                    className="game-card group"
                  >
                    <div className="text-6xl mb-4 group-hover:scale-110 transition-transform">
                      ⭐
                    </div>
                    <h3 className="text-2xl font-bold text-pink-600 mb-2">
                      Missing Letter
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Fill in the missing letter to complete the word!
                    </p>
                    <div className="text-sm text-gray-500">
                      🔤 Spelling • 🧩 Puzzle • 💡 Logic
                    </div>
                  </button>
                </div>
              </div>

              {/* Stats Section */}
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white rounded-3xl shadow-lg p-6 text-center">
                  <div className="flex items-center justify-center mb-2">
                    <TrendingUp className="text-blue-500" size={32} />
                  </div>
                  <div className="text-4xl font-bold text-blue-600 mb-1">
                    {userProgress?.gamesPlayed || 0}
                  </div>
                  <div className="text-gray-600">Games Played</div>
                </div>

                <div className="bg-white rounded-3xl shadow-lg p-6 text-center">
                  <div className="flex items-center justify-center mb-2">
                    <Star className="text-yellow-500 fill-yellow-500" size={32} />
                  </div>
                  <div className="text-4xl font-bold text-yellow-600 mb-1">
                    {userProgress?.wordsLearned.length || 0}
                  </div>
                  <div className="text-gray-600">Words Learned</div>
                </div>

                <div className="bg-white rounded-3xl shadow-lg p-6 text-center">
                  <div className="flex items-center justify-center mb-2">
                    <User className="text-purple-500" size={32} />
                  </div>
                  <div className="text-4xl mb-1">{userProfile.avatar}</div>
                  <div className="text-gray-600">{userProfile.name}</div>
                </div>
              </div>

              {/* Admin Dashboard Button */}
              <div className="mt-6">
                <button
                  onClick={() => setCurrentView('admin')}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-3xl shadow-lg p-6 hover:shadow-xl transition-all duration-200 hover:scale-[1.02]"
                >
                  <div className="flex items-center justify-center gap-4">
                    <BarChart3 size={32} />
                    <div className="text-left">
                      <div className="text-2xl font-bold">Parent Dashboard</div>
                      <div className="text-purple-100 text-sm">
                        View detailed progress, stats, and manage settings
                      </div>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          )}

          {currentView === 'level-select' && selectedGameType && (
            <LevelSelect
              gameType={selectedGameType}
              onLevelSelect={handleLevelSelect}
              onBack={handleBackToHome}
            />
          )}

          {currentView === 'admin' && (
            <AdminDashboard onBack={handleBackToHome} />
          )}

          {currentView === 'game' && gameState.isPlaying && renderGame()}

          {/* Show home if game ended */}
          {currentView === 'game' && !gameState.isPlaying && (
            <div className="bg-white rounded-3xl shadow-2xl p-12 text-center">
              <div className="text-6xl mb-6">🎉</div>
              <h2 className="text-4xl font-bold text-primary mb-4">Game Complete!</h2>
              <p className="text-xl text-gray-600 mb-8">
                Great job! Ready for another round?
              </p>
              <button onClick={handleBackToHome} className="btn-primary text-xl px-8 py-4">
                Back to Home
              </button>
            </div>
          )}
        </main>

        {/* Footer */}
        <footer className="text-center mt-12 text-white opacity-90">
          <p className="text-lg">
            Made with ❤️ for kids learning languages around the world 🌍
          </p>
          <p className="text-sm mt-2 opacity-75">
            Open Source • MIT License • Contributions Welcome
          </p>
        </footer>
      </div>

      {/* Celebration overlay */}
      <Celebration
        show={gameState.showCelebration}
        title="Amazing!"
        message="You're doing great!"
      />

      {/* Settings Drawer */}
      <SettingsDrawer isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  );
}

function App() {
  return (
    <AppProvider>
      <AppContent />
    </AppProvider>
  );
}

export default App;
